import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Route, Routes, useParams } from 'react-router-dom';

import { VariableProvider } from '@/contexts/VariableContext';
import { Criticality, VariableInfo } from '@/lib/App';
import { fetchVariable } from '@/store/state/variables/actions';
import { variableSelector } from '@/store/state/variables/selectors';
import { AppDispatch, AppState } from '@/store/types';

import './RolloutPage.css';

import { Flex, Skeleton, Tooltip, Typography } from '@duxton-web/ui';
import { PageHeaderInfo, PageHeaderTagProps } from '@web-platform/page-header';

import RolloutApprovalRequest from '../RolloutApprovalRequest';
import RolloutTabV2 from '../RolloutTab/RolloutTabV2';
import { SecondaryText } from '@/common/components';
import { FeedbackType, openInAppFeedback } from '@/common/inAppFeedback';
import { VariableState } from '@/constants';
import {
  ALL_TENANT_CONFIGS,
  GRAB_TENANT_SEQ,
  hasSyncedToTenant,
} from '@/features/tenant-sync';
import {
  buildAccessTargetFromVariable,
  PermissionUtils,
  UserAction,
} from '@/utils/accessControl';

// interface RolloutPageParams {
//   variableName: string;
//   id: string;
// }

interface DispatchProps {
  fetchVariable: (variableName: string) => void;
}

interface StateProps {
  variable: VariableInfo | null;
}

type OwnProps = Record<string, never>;

type RolloutPageProps = OwnProps & StateProps & DispatchProps;

export const incideReportLink = 'https://go/gmmincidentreports';

export function renderTags(
  inVariable: VariableInfo,
  permissionUtils?: PermissionUtils,
): PageHeaderTagProps[] {
  const isVariableCritical = inVariable?.criticality === Criticality.Critical;
  const isVariableArchived = inVariable?.state === VariableState.ARCHIVED;

  const tags: PageHeaderTagProps[] = [];

  if (isVariableCritical)
    tags.push({
      content: 'critical',
      tooltip: (
        <div>
          {' '}
          Config values of this variable that can cause a High Impact Incident
          or a Medium Impact Incident as defined in
          <a href={incideReportLink}> incident report </a>. Be very careful in
          making changes to this variable.
        </div>
      ),
      color: 'red',
    });

  if (isVariableArchived)
    tags.push({
      content: 'archived',
      color: 'red',
      tooltip:
        'This variable is archived. No rollout can be configured on archived variable.',
    });

  const managedByPlatform = inVariable?.metadata?.managedByPlatform;
  if (managedByPlatform) {
    tags.push({
      content: `Managed by: ${managedByPlatform}`,
      color: 'purple',
      tooltip: `Rollout is managed by ${managedByPlatform}. You can still modify variable settings.`,
    });
  }

  // Tenant sync badges
  ALL_TENANT_CONFIGS.forEach((config) => {
    if (hasSyncedToTenant(inVariable?.targetTenantSeqs, config.tenantSeq)) {
      tags.push({
        content: `Synced to ${config.name}`,
        color: config.badgeColor,
        tooltip: `This variable has been synced to ${config.name} portal.`,
      });
    }
  });

  if (
    ALL_TENANT_CONFIGS.some((c) => c.isCurrentTenant()) &&
    inVariable?.sourceTenantSeq === GRAB_TENANT_SEQ
  ) {
    tags.push({
      content: 'Synced from Grab',
      color: 'purple',
      tooltip:
        'This variable is synced from Grab. Metadata can only be edited on the Grab side.',
    });
  }

  if (!permissionUtils) return tags;

  // Do not check edit access if variable is archived
  if (isVariableArchived) return tags;

  const [canCreateRollout, explanation] = permissionUtils.generateExplanation({
    action: UserAction.CREATE_ROLLOUT_DIFF_FOR_CURRENT_VARIABLE,
    techFamilies: [inVariable.techFamily],
    accessTargets: [buildAccessTargetFromVariable(inVariable)],
  });

  if (!canCreateRollout) {
    tags.push({
      content: 'no edit access',
      color: 'red',
      tooltip: explanation,
    });
  }

  return tags;
}

export const renderInfos = (variable: VariableInfo): PageHeaderInfo[] =>
  [
    {
      content: variable.type.toUpperCase(),
      bold: true,
    },
    {
      title: 'Product Name',
      content: variable.productEntity,
    },
    {
      title: 'Engineering Owner',
      content: variable?.metadata?.technicalOwner,
    },
    {
      title: 'Product Owner',
      content: variable?.metadata?.productOwner,
    },
    variable?.metadata?.documentUrl && {
      content: (
        <Flex gap={4} wrap="wrap">
          <SecondaryText
            style={{
              color: '#3D3D3D',
              fontWeight: 600,
              minWidth: 'fit-content',
            }}
          >
            Documentation Link:
          </SecondaryText>
          {variable?.metadata?.documentUrl
            ?.split(',')
            .map((url: string, index: number) => (
              <Flex key={url}>
                <Tooltip title={url}>
                  <Typography.Link
                    underline
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ fontSize: '12px' }}
                    ellipsis
                  >
                    {`${url.substr(0, 50)}${url.length > 50 ? '...' : ''}`}$
                  </Typography.Link>
                </Tooltip>

                {index < variable.metadata.documentUrl.split(',').length - 1
                  ? ','
                  : ''}
              </Flex>
            ))}
        </Flex>
      ),
    },
  ].filter(Boolean);

// Component to handle approval request route with params
const ApprovalRequestRoute = () => {
  const { id } = useParams<{ id: string }>();
  return <RolloutApprovalRequest diffId={id!} />;
};

export const RolloutPage = ({ fetchVariable, variable }: RolloutPageProps) => {
  const { variableName, variableId } = useParams<{
    variableName?: string;
    variableId?: string;
  }>();
  const actualVariableName = variableName || variableId;
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    fetchVariable(actualVariableName);

    openInAppFeedback({
      type: FeedbackType.CSAT,
      context: 'variableRolloutPage',
      event: 'rolloutPageViewed',
    });
  }, [variableName]);

  // Check if variable is fully loaded with all required properties
  if (!variable && actualVariableName) {
    return <Skeleton />;
  }

  if (!variable) {
    return <Skeleton />;
  }

  return (
    <VariableProvider variable={variable}>
      <div
        className={`rollout-page__body${isEditing ? '--editing' : ''}`}
        style={{
          height: '100%',
        }}
      >
        <Routes>
          <Route
            path="approve-request/:id"
            element={<ApprovalRequestRoute />}
          />
          <Route
            path="changelog"
            element={
              <RolloutTabV2
                showChangelog
                onEditRollout={() => setIsEditing(true)}
              />
            }
          />
          <Route
            path="search-by-constraints"
            element={
              <RolloutTabV2
                showSearchByConstraints
                onEditRollout={() => setIsEditing(true)}
              />
            }
          />
          <Route
            path={`:action`}
            element={<RolloutTabV2 onEditRollout={() => setIsEditing(true)} />}
          />
          <Route
            path="*"
            element={<RolloutTabV2 onEditRollout={() => setIsEditing(true)} />}
          />
        </Routes>
      </div>
    </VariableProvider>
  );
};

function mapStateToProps(state: AppState, _ownProps: any): StateProps {
  const pathname = window.location.pathname;
  const variableNameMatch = pathname.match(/\/variables\/([^/]+)/);
  const variableName = variableNameMatch ? variableNameMatch[1] : null;

  return {
    variable: variableName ? variableSelector(state, variableName) : null,
  };
}

function mapDispatchToProps(dispatch: AppDispatch): DispatchProps {
  return {
    fetchVariable: (variableName: string) =>
      dispatch(fetchVariable(variableName)),
  };
}

export default connect<StateProps, DispatchProps, OwnProps, AppState>(
  mapStateToProps,
  mapDispatchToProps,
)(RolloutPage);
